export const KEYPAD_BUTTONS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '*',
  '0',
  '#',
] as const
type KeypadButtonTuple = typeof KEYPAD_BUTTONS
type KeypadButton = KeypadButtonTuple[number]

export function isKeypadButton(value: string): value is KeypadButton {
  return KEYPAD_BUTTONS.includes(value as KeypadButton)
}

export const isPhoneNumber = (value: string) => {
  const cleanedValue = value.replace(/[^0-9A-Za-z]+/g, '')
  return /^[0-9]+$/g.test(cleanedValue)
}

// Characters the user actually types; formatting only inserts/removes the rest
// (spaces, parens, dashes) around them.
const MEANINGFUL_CHAR = /[0-9+*#]/

const meaningfulChars = (value: string) =>
  [...value].filter((char) => MEANINGFUL_CHAR.test(char))

const sharedPrefixLength = (a: string[], b: string[]) => {
  let i = 0
  while (i < a.length && i < b.length && a[i] === b[i]) i++
  return i
}

// After the value is reformatted on each keystroke, place the caret just after
// the same number of meaningful characters it followed in the raw input.
// Anchoring to the digit count (rather than diffing the two strings) keeps the
// caret correct for any locale grouping — e.g. UK national numbers past 8
// digits, whose spacing differs from the US template.
// Formatting can also add or drop a digit (a UK trunk 0, a US 1). When that
// happens before the caret, the target count shifts by the change in
// meaningful characters.
export const getCaretPositionAfterFormatting = (
  rawValue: string,
  caretIndex: number,
  formattedValue: string,
): number => {
  let rawMeaningfulBeforeCaret = 0
  for (let i = 0; i < caretIndex && i < rawValue.length; i++) {
    if (MEANINGFUL_CHAR.test(rawValue[i])) rawMeaningfulBeforeCaret++
  }

  const rawMeaningful = meaningfulChars(rawValue)
  const formattedMeaningful = meaningfulChars(formattedValue)
  const changedBeforeCaret =
    rawMeaningfulBeforeCaret >
    sharedPrefixLength(rawMeaningful, formattedMeaningful)
  const meaningfulBeforeCaret = changedBeforeCaret
    ? rawMeaningfulBeforeCaret +
      formattedMeaningful.length -
      rawMeaningful.length
    : rawMeaningfulBeforeCaret

  if (meaningfulBeforeCaret <= 0) return 0

  let seen = 0
  for (let i = 0; i < formattedValue.length; i++) {
    if (MEANINGFUL_CHAR.test(formattedValue[i])) {
      seen++
      if (seen === meaningfulBeforeCaret) return i + 1
    }
  }

  return formattedValue.length
}

export const normalizeWhitespace = (value: string) =>
  value.replace(/\s+/g, ' ').trim()
